<template>
    <div class="custom-chart">
        <div v-if="showIntervalArrows && showLeftArrow" @click="getDataInInterval(true)" class="chart-arrow left">
            <b-icon-caret-left></b-icon-caret-left>
        </div>
        <highcharts ref="highcharts"   :options="localChartOptions"></highcharts>
        <div v-if="showIntervalArrows && showRightArrow" @click="getDataInInterval(false)" class="chart-arrow right">
            <b-icon-caret-right></b-icon-caret-right>
        </div>
    </div>
</template>

<script>
import '@/plugins/highchartsLanguageConfig'
import {
BACKGROUND_COLOUR
} from '@/constants/colours'
import {deepClone} from '@/helpers/clone.helper'

export default {
   name: 'Chart',
   props: {
    chartOptions: {
      type: Object,
      required: true
    },
    showIntervalArrows: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default () {
        return window.screen.width < 1000 ? 15 : 20
      }
    },
    dynamicXAxis: {
      type: Boolean,
      default: false
    },
    xAxisDayInterval: {
      type: Number,
      default: null
    },
    applyGradient: {
      type: Boolean,
      default: false
    },
    scrollablePlotAreaOptions: {
      type: Object,
      default: () => ({})
    },
    chartType: {
      type: String,
      default: null
    },
    chartBackgroundColor: {
      type: String,
      default: BACKGROUND_COLOUR // Color por defecto explícito
    },
    legendOptions: {
      type: Object,
      default: () => ({})
    },
    chartTitle: {
      type: String,
      default: null
    },
    titleOptions: {
      type: Object,
      default: () => ({})
    },
    markersEnabled: {
      type: Boolean,
      default: false
    },
    markerRadius: {
      type: Number,
      default: 5
    },
     monthWindow: { // 0 = desactivado ⇒ paginación anterior
      type: Number,
      default: 6
    }
   },
   watch: {
     chartOptions: {
       handler (newVal) {
        this.localChartOptions = deepClone(newVal)
         this.processData()
         this.$nextTick(() => this.initDateWindow())
       },
       deep: true,
       immediate: true
     },
     page () {
       this.updateXAxisTickIntervals()
       this.updateGradient()
       this.updateScrollablePlotArea()
     },
     xAxisDayInterval () {
       this.updateXAxisTickIntervals()
     },
     dynamicXAxis () {
       this.updateXAxisTickIntervals()
     },
     applyGradient () {
       this.updateGradient()
     },
     scrollablePlotAreaOptions: {
       handler () {
         this.updateScrollablePlotArea()
       },
       deep: true
     },
     chartType () {
       this.updateChartType()
       this.updateGradient()
     },
     chartBackgroundColor () {
       this.updateChartBackground()
     },
     legendOptions: {
       handler () {
         this.updateLegendConfig()
       },
       deep: true
     },
     chartTitle () {
       this.updateTitle()
     },
     titleOptions: {
       handler () {
         this.updateTitleConfig()
       },
       deep: true
     },
     'localChartOptions.series': {
       handler () {
         this.updateGradient()
       },
       deep: true
     },
     markersEnabled () { this.updateMarkerConfig() },
    markerRadius () { this.updateMarkerConfig() },
     monthWindow () { this.initDateWindow() }
   },
   data () {
     return {
       localChartOptions: JSON.parse(JSON.stringify(this.chartOptions)), // Copia inicial
       showLeftArrow: true,
       showRightArrow: false,
       dataPerPage: [],
       page: 0
     }
   },
   created () {
     this.processData()
     this.showLeftArrow = this.dataPerPage.length > 1
     this.updateXAxisTickIntervals()
     this.updateGradient()
     this.updateScrollablePlotArea()
     this.updateChartType()
     this.updateChartBackground()
     this.updateLegendConfig()
     this.updateTitle()
     this.updateTitleConfig()
     this.updateXAxisLabels()
     this.updateMarkerConfig()
   },
   mounted () {
     this.initDateWindow()
   },
   methods: {
     processData () {
       const series = this.localChartOptions.series[0] // Usar copia local
       if (this.monthWindow) return
       if (!series.data.length) return
       const data = series.data
       const result = []
       let aux = []
       let count = 1
       for (let i = data.length - 1; i >= 0; i--) {
         aux.unshift(data[i])
         if (count === this.itemsPerPage || i === 0) {
           result.push(aux)
           aux = []
           count = 0
         }
         count++
       }
       this.dataPerPage = result
       this.localChartOptions.series[0].data = this.dataPerPage[this.page]
     },
     initDateWindow () {
      if (!this.monthWindow) return // 0 ⇒ usa paginado clásico

      // collect all points
      const seriesArr = this.localChartOptions.series || []
      const pts = seriesArr.flatMap(s => s.data || [])
      if (!pts.length) return

      this.seriesMin = Math.min(...pts.map(p => p.x))
      this.seriesMax = Math.max(...pts.map(p => p.x))

      const wMs = this.monthWindow * 30 * 24 * 3600 * 1000
      this.currentEnd = this.seriesMax
      this.currentStart = Math.max(this.seriesMin, this.seriesMax - wMs)
      this.setWindow()
    },
    setWindow () {
      const chart = this.$refs.highcharts.chart
      if (!chart) return
      chart.xAxis[0].setExtremes(this.currentStart, this.currentEnd) // Highcharts API
      this.updateArrows()
    },
    updateArrows () {
      if (!this.monthWindow) return
      const oneDay = 24 * 3600 * 1000 // evita falsos negativos por milisegundos
      this.showLeftArrow = (this.currentStart - this.seriesMin) >= oneDay
      this.showRightArrow = (this.seriesMax - this.currentEnd) >= oneDay
      /* evitas doble paginación */
      if (this.showLeftArrow || this.showRightArrow) this.page = 0
    },
     getDataInInterval (previous) {
      /* Si monthWindow = 0 ⇒ paginación por puntos */
      if (!this.monthWindow) {
        this.page += previous ? 1 : -1
        this.localChartOptions.series[0].data = this.dataPerPage[this.page]
        this.showLeftArrow = this.page < this.dataPerPage.length - 1
        this.showRightArrow = this.page > 0
        return
      }
      const wMs = this.monthWindow * 30 * 24 * 3600 * 1000
      if (previous) { // flecha izquierda
        this.currentEnd = this.currentStart
        this.currentStart = Math.max(this.seriesMin, this.currentEnd - wMs)
      } else { // flecha derecha
        this.currentStart = this.currentEnd
        this.currentEnd = Math.min(this.seriesMax, this.currentStart + wMs)
      }
      this.setWindow()
    },
     updateXAxisTickIntervals () {
       const xAxis = this.localChartOptions.xAxis
       if (!xAxis || xAxis.type !== 'datetime') return
       const dayMs = 24 * 3600 * 1000
       if (this.xAxisDayInterval) {
         xAxis.tickInterval = this.xAxisDayInterval * dayMs
       } else if (this.dynamicXAxis) {
         const data = this.localChartOptions.series[0].data
         if (!data || data.length < 2) return
         const rangeDays = Math.ceil((data[data.length - 1].x - data[0].x) / dayMs)
         xAxis.tickInterval = rangeDays * dayMs
       }
     },
     updateXAxisLabels () {
       const xAxis = this.localChartOptions.xAxis
       if (!xAxis || xAxis.type !== 'datetime') return
       xAxis.labels = {
         ...(xAxis.labels || {}),
         rotation: 45,
         format: '{value:%e %b %Y}'
       }
     },
     updateGradient () {
       if (!this.applyGradient) return
       const seriesList = this.localChartOptions.series || []
       const baseType = this.chartType || this.localChartOptions.chart.type
       const areaSeries = seriesList.find(s => (s.type || baseType) === 'areaspline' && s.data && s.data.length)
       if (!areaSeries) return
       const xs = areaSeries.data.map(pt => pt.x)
       const minX = Math.min(...xs)
       const maxX = Math.max(...xs)
       const total = maxX - minX || 1
       const stops = areaSeries.data.map(pt => [(pt.x - minX) / total, pt.color || '#000'])
       areaSeries.fillColor = { linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 }, stops }
     },
     updateScrollablePlotArea () {
       const chartCfg = this.localChartOptions.chart || {}
       chartCfg.scrollablePlotArea = { ...(chartCfg.scrollablePlotArea || {}), ...this.scrollablePlotAreaOptions }
       this.localChartOptions.chart = chartCfg
     },
     updateChartType () {
       if (!this.chartType) return
       const chartCfg = this.localChartOptions.chart || {}
       chartCfg.type = this.chartType
       this.localChartOptions.chart = chartCfg
     },
     updateChartBackground () {
       const chartCfg = this.localChartOptions.chart || {}
       chartCfg.backgroundColor = this.chartBackgroundColor
       this.localChartOptions.chart = chartCfg
     },
     updateLegendConfig () {
       const defaultLeg = this.localChartOptions.legend || {}
       this.localChartOptions.legend = { ...defaultLeg, ...this.legendOptions }
     },
     updateTitle () {
       if (!this.chartTitle) return
       const titleCfg = this.localChartOptions.title || {}
       titleCfg.text = this.chartTitle
       this.localChartOptions.title = titleCfg
     },
     updateTitleConfig () {
       const titleCfg = this.localChartOptions.title || {}
       this.localChartOptions.title = { ...titleCfg, ...this.titleOptions }
     },
     updateMarkerConfig () {
      const cfg = { ...(this.localChartOptions.plotOptions || {}) }
      cfg.series = cfg.series || {}
      cfg.series.marker = {
        enabled: this.markersEnabled,
        radius: this.markerRadius
      }
      this.localChartOptions.plotOptions = cfg
    }
   }
}
</script>

<style lang="scss">
.custom-chart { position: relative; }
.chart-arrow { position: absolute; font-size: 11px; bottom: 0; z-index: 5; cursor: pointer; }
// .highcharts-legend-item-hidden { display: none; }
.chart-arrow.left  { left:  0; }
.chart-arrow.right { right: 0; }
</style>


// WEBPACK FOOTER //
// src/components/base/Chart.vue